import type { Stats } from "../../lib/types";

/** The presenter-side rendering of each slide type's answers. */

interface Props {
  type: string;
  config: Record<string, any>;
  stats: Stats | null;
  accent: string;
  revealed: boolean;
  reveal: Record<string, any>;
  /** Headcount, so the two types with nothing to draw yet can still show
   *  live progress instead of a placeholder. */
  joined?: number;
}

interface ChartProps {
  config: Record<string, any>;
  stats: Stats;
  accent: string;
  revealed: boolean;
  reveal: Record<string, any>;
}

export function Results({ type, config, stats, accent, revealed, reveal, joined }: Props) {
  if (!stats) return null;

  switch (type) {
    case "multiple":
    case "quiz":
      return <Bars {...{ config, stats, accent, revealed, reveal, isQuiz: type === "quiz" }} />;
    case "wordcloud":
      return <WordCloud stats={stats} accent={accent} joined={joined} />;
    case "scale":
      return <ScaleChart config={config} stats={stats} accent={accent} />;
    case "ranking":
      return <RankingChart config={config} stats={stats} accent={accent} />;
    case "guess":
      return <GuessChart config={config} stats={stats} accent={accent} revealed={revealed} />;
    case "grid":
      return <GridChart config={config} stats={stats} accent={accent} />;
    case "open":
      return <OpenCards stats={stats} accent={accent} joined={joined} />;
    default:
      return null;
  }
}

/** For the two types that have literally nothing to draw before the first
 *  answer. Shows live counts rather than a dead "waiting" line. */
function Listening({ total, joined }: { total: number; joined?: number }) {
  return (
    <div style={{ textAlign: "center", padding: "40px 0", width: "100%" }}>
      <span
        style={{
          display: "inline-block",
          width: 10,
          height: 10,
          borderRadius: "var(--radius-pill)",
          background: "var(--success)",
          animation: "breathe 2.4s var(--ease-default) infinite",
        }}
      />
      <p className="muted" style={{ marginTop: 12, fontSize: 16 }}>
        {joined ? `${total} of ${joined} answered` : "Answers appear here as they arrive"}
      </p>
    </div>
  );
}

function Bars({
  config,
  stats,
  accent,
  revealed,
  reveal,
  isQuiz,
}: ChartProps & { isQuiz: boolean }) {
  const options: string[] = config.options ?? [];
  const counts = stats.counts ?? [];
  const percents = stats.percents ?? [];
  const correct = revealed ? (reveal.correct as number | undefined) : undefined;
  const max = Math.max(1, ...counts);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, width: "100%" }}>
      {stats.multi && (
        <p className="tiny" style={{ marginTop: -4 }}>
          Multiple answers allowed — percentages are share of the room, so they
          can add up past 100%.
        </p>
      )}
      {options.map((option, i) => {
        const isRight = isQuiz && correct === i;
        const isWrong = isQuiz && correct !== undefined && !isRight;
        return (
          <div key={i}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 6,
                fontSize: 17,
                fontWeight: 600,
                opacity: isWrong ? 0.45 : 1,
              }}
            >
              <span>
                {isRight && <span style={{ color: "var(--success)" }}>✓ </span>}
                {option}
              </span>
              <span style={{ color: "var(--text-secondary)" }}>
                {percents[i] ?? 0}% · {counts[i] ?? 0}
              </span>
            </div>
            <div
              style={{
                height: 22,
                borderRadius: "var(--radius-pill)",
                background: "var(--surface-variant)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${((counts[i] ?? 0) / max) * 100}%`,
                  background: isRight ? "var(--success)" : accent,
                  opacity: isWrong ? 0.35 : 1,
                  borderRadius: "var(--radius-pill)",
                  transition: "width 420ms var(--ease-default)",
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function WordCloud({
  stats,
  accent,
  joined,
}: {
  stats: Stats;
  accent: string;
  joined?: number;
}) {
  const words = stats.words ?? [];
  if (!words.length) return <Listening total={stats.total} joined={joined} />;
  const top = words[0].count;

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "10px 20px",
        justifyContent: "center",
        alignItems: "center",
        padding: 20,
      }}
    >
      {words.map((w, i) => (
        <span
          key={w.word}
          title={`${w.count}×`}
          style={{
            // Repeats grow the word — the whole point of a word cloud.
            fontSize: 20 + (w.count / top) * 48,
            fontWeight: 700,
            letterSpacing: "-0.02em",
            color: i % 2 ? accent : "var(--text-primary)",
            opacity: 0.55 + (w.count / top) * 0.45,
            animation: "cardIn 400ms var(--ease-default)",
          }}
        >
          {w.word}
        </span>
      ))}
    </div>
  );
}

function ScaleChart({ config, stats, accent }: Omit<ChartProps, "revealed" | "reveal">) {
  const distribution = stats.distribution ?? [0, 0, 0, 0, 0];
  const max = Math.max(1, ...distribution);

  return (
    <div style={{ width: "100%" }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 14, height: 200 }}>
        {distribution.map((count, i) => (
          <div key={i} style={{ flex: 1, textAlign: "center" }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{count}</div>
            <div
              style={{
                height: `${(count / max) * 150}px`,
                minHeight: 4,
                background: accent,
                borderRadius: 10,
                transition: "height 420ms var(--ease-default)",
              }}
            />
            <div style={{ marginTop: 8, fontWeight: 600 }}>{i + 1}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
        <span className="muted">{config.min_label}</span>
        {stats.average != null && (
          <span style={{ fontWeight: 700, color: accent }}>average {stats.average} / 5</span>
        )}
        <span className="muted">{config.max_label}</span>
      </div>
    </div>
  );
}

const RANK_ROW = 58;
const RANK_GAP = 10;

function RankingChart({ config, stats, accent }: Omit<ChartProps, "revealed" | "reveal">) {
  const options: string[] = config.options ?? [];
  const averages = stats.averages ?? [];

  // Every option is on screen from the first second. Options nobody has
  // ranked yet sort to the bottom in their original order rather than
  // hiding the whole board behind an empty state.
  const order = options
    .map((label, index) => ({ label, index, average: averages[index] }))
    .sort(
      (a, b) =>
        (a.average ?? Number.POSITIVE_INFINITY) - (b.average ?? Number.POSITIVE_INFINITY) ||
        a.index - b.index,
    );
  const placeOf = new Map(order.map((row, place) => [row.index, place]));

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: options.length * (RANK_ROW + RANK_GAP) - RANK_GAP,
      }}
    >
      {options.map((label, index) => {
        const place = placeOf.get(index) ?? index;
        const average = averages[index];
        const leading = place === 0 && average != null;
        return (
          // Keyed by option, positioned by transform: React keeps the same
          // node and CSS slides it, so a row that overtakes another is
          // visibly a row moving rather than a list redrawing.
          <div
            key={index}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: RANK_ROW,
              display: "flex",
              alignItems: "center",
              gap: 14,
              padding: "0 18px",
              borderRadius: 16,
              background: "var(--surface)",
              border: `0.5px solid ${leading ? accent : "var(--outline)"}`,
              transform: `translateY(${place * (RANK_ROW + RANK_GAP)}px)`,
              transition:
                "transform 520ms var(--ease-default), border-color 300ms var(--ease-default)",
            }}
          >
            <span
              style={{
                width: 30,
                height: 30,
                display: "grid",
                placeItems: "center",
                borderRadius: "var(--radius-pill)",
                background: leading ? accent : "var(--surface-variant)",
                color: leading ? "#fff" : "var(--text-secondary)",
                fontWeight: 700,
                flex: "none",
                transition: "background 300ms var(--ease-default)",
              }}
            >
              {place + 1}
            </span>
            <span style={{ flex: 1, fontSize: 17, fontWeight: 600 }}>{label}</span>
            <span className="muted" style={{ flex: "none" }}>
              {average == null ? "—" : `avg ${average}`}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function GuessChart({
  config,
  stats,
  accent,
  revealed,
}: Omit<ChartProps, "reveal">) {
  // Drawn even at zero, so the range is readable before the first guess
  // and the bars grow into a frame the room has already seen.
  const bins = stats.bins?.length ? stats.bins : new Array(12).fill(0);
  const max = Math.max(1, ...bins);

  return (
    <div style={{ width: "100%" }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 5, height: 170 }}>
        {bins.map((count, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: `${(count / max) * 100}%`,
              minHeight: 3,
              background: accent,
              opacity: 0.35 + (count / max) * 0.65,
              borderRadius: 6,
              transition: "height 420ms var(--ease-default)",
            }}
          />
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
        <span className="tiny">{config.lo}</span>
        <span className="tiny">{config.hi}</span>
      </div>
      <div
        style={{
          display: "flex",
          gap: 26,
          justifyContent: "center",
          marginTop: 20,
          flexWrap: "wrap",
        }}
      >
        <Stat label="average guess" value={stats.average ?? "—"} />
        {revealed && <Stat label="answer" value={config.answer} accent="var(--success)" />}
        {revealed && stats.closest && (
          <Stat label="closest" value={`${stats.closest.name} · ${stats.closest.value}`} />
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: unknown; accent?: string }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 30, fontWeight: 700, color: accent ?? "var(--text-primary)" }}>
        {String(value)}
      </div>
      <div className="tiny">{label}</div>
    </div>
  );
}

function GridChart({ config, stats, accent }: Omit<ChartProps, "revealed" | "reveal">) {
  const points = stats.points ?? [];
  return (
    <div style={{ width: "100%", maxWidth: 520, margin: "0 auto" }}>
      <div style={{ textAlign: "center", marginBottom: 6 }} className="muted">
        {config.y_hi}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span className="muted" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
          {config.x_lo}
        </span>
        <div
          style={{
            position: "relative",
            flex: 1,
            aspectRatio: "1",
            background: "var(--surface)",
            border: "0.5px solid var(--outline)",
            borderRadius: 16,
          }}
        >
          <div style={axis("row")} />
          <div style={axis("col")} />
          {points.map((point, i) => (
            <span
              key={i}
              title={point.name}
              style={{
                position: "absolute",
                left: `${point.x * 100}%`,
                top: `${point.y * 100}%`,
                width: 13,
                height: 13,
                marginLeft: -6.5,
                marginTop: -6.5,
                borderRadius: "var(--radius-pill)",
                background: accent,
                opacity: 0.75,
                animation: "dotIn 300ms var(--ease-default)",
              }}
            />
          ))}
        </div>
        <span className="muted" style={{ writingMode: "vertical-rl" }}>
          {config.x_hi}
        </span>
      </div>
      <div style={{ textAlign: "center", marginTop: 6 }} className="muted">
        {config.y_lo}
      </div>
    </div>
  );
}

function axis(direction: "row" | "col"): React.CSSProperties {
  return direction === "row"
    ? { position: "absolute", left: 0, right: 0, top: "50%", borderTop: "1px dashed var(--outline)" }
    : { position: "absolute", top: 0, bottom: 0, left: "50%", borderLeft: "1px dashed var(--outline)" };
}

function OpenCards({
  stats,
  accent,
  joined,
}: {
  stats: Stats;
  accent: string;
  joined?: number;
}) {
  const answers = stats.answers ?? [];
  if (!answers.length) return <Listening total={stats.total} joined={joined} />;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
        gap: 12,
        width: "100%",
      }}
    >
      {answers.map((answer, i) => (
        <div
          key={i}
          style={{
            padding: 16,
            borderRadius: 16,
            background: "var(--surface)",
            borderLeft: `3px solid ${accent}`,
            border: "0.5px solid var(--outline)",
            animation: "cardIn 300ms var(--ease-default)",
          }}
        >
          <p style={{ fontSize: 15, lineHeight: 1.45 }}>{answer.text}</p>
          <div className="tiny" style={{ marginTop: 8 }}>
            {answer.name}
          </div>
        </div>
      ))}
    </div>
  );
}
